import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getSpecsLists } from 'services/cataloge';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './SpecDropdown.scss';

interface ISpecDropdown {
  label: string;
  value: string | undefined;
  /** Opciones estáticas. Usar cuando el padre ya tiene los valores cargados. */
  options?: string[];
  /** Nombre del parámetro para cargar las opciones desde el backend vía getSpecsLists. */
  paramName?: string;
  placeholder?: string;
  required?: boolean;
  onChange: (value: string) => void;
}

/**
 * Selector de especificación técnica con filtro local.
 * Acepta opciones estáticas via `options`, o las carga automáticamente si se provee `paramName`.
 * No preselecciona ninguna opción: el valor inicial es siempre vacío hasta que el usuario elija.
 * El dropdown se renderiza en un portal para escapar el overflow:hidden del contenedor padre.
 */
const SpecDropdown = ({
  label,
  value,
  options,
  paramName,
  placeholder = 'Seleccionar',
  required,
  onChange
}: ISpecDropdown) => {
  const [searchString, setSearchString] = useState(value ?? '');
  const [isOpen, setIsOpen] = useState(false);
  const [loadedOptions, setLoadedOptions] = useState<string[]>(options ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value ?? '');

  const isFilled = !!value;

  const filteredOptions = loadedOptions.filter(opt =>
    opt.toLowerCase().includes(searchString.toLowerCase())
  );

  /** Mantiene valueRef sincronizado para que handleClickOutside siempre lea el valor actual. */
  useEffect(() => {
    valueRef.current = value ?? '';
    setSearchString(value ?? '');
  }, [value]);

  /** Actualiza las opciones cuando el prop estático cambia. */
  useEffect(() => {
    if (options) setLoadedOptions(options);
  }, [options]);

  /** Carga las opciones desde el backend si se provee paramName (y no hay options estáticas). */
  useEffect(() => {
    if (!paramName || options) return;
    setIsLoading(true);
    getSpecsLists(paramName)
      .then(data => setLoadedOptions(data.value ?? []))
      .catch(() => displayErrorNotification())
      .finally(() => setIsLoading(false));
  }, [paramName]);

  /**
   * Cierra el dropdown al hacer click fuera. Se crea una sola vez (deps: []).
   * Lee valueRef.current en lugar de capturar value del closure para evitar stale state.
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const isInsideContainer = containerRef.current?.contains(e.target as Node);
      const isInsideDropdown = dropdownRef.current?.contains(e.target as Node);
      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
        setSearchString(valueRef.current);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Calcula la posición del dropdown midiendo el espacio disponible arriba y abajo del input.
   * Si hay más espacio arriba (o no cabe mínimamente abajo), abre hacia arriba (flip).
   */
  const updateDropdownPosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const maxHeight = 200;
    const minUsable = 120;

    if (spaceBelow >= minUsable || spaceBelow >= spaceAbove) {
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(maxHeight, spaceBelow),
        zIndex: 9999
      });
    } else {
      const availableHeight = Math.min(maxHeight, spaceAbove);
      setDropdownStyle({
        position: 'fixed',
        top: rect.top - availableHeight - 4,
        left: rect.left,
        width: rect.width,
        maxHeight: availableHeight,
        zIndex: 9999
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    setIsOpen(true);
  };

  const handleFocus = () => {
    updateDropdownPosition();
    setIsOpen(true);
  };

  const handleSelect = (option: string) => {
    setSearchString(option);
    setIsOpen(false);
    onChange(option);
  };

  return (
    <div className={`specDropdown${isFilled ? ' filled' : ''}`} ref={containerRef}>
      <label className="fieldLabel">
        {label}{required && '*'}
      </label>
      <div className="inputWrapper">
        <input
          ref={inputRef}
          value={searchString}
          placeholder={isLoading ? 'Cargando...' : placeholder}
          disabled={isLoading}
          onChange={handleInputChange}
          onFocus={handleFocus}
          autoComplete="off"
        />
      </div>
      {isOpen && createPortal(
        <div className="specDropdownList" style={dropdownStyle} ref={dropdownRef}>
          {filteredOptions.length === 0 && (
            <div className="dropdownItem empty">Sin resultados</div>
          )}
          {filteredOptions.map(opt => (
            <div
              key={opt}
              className="dropdownItem"
              onMouseDown={() => handleSelect(opt)}
            >
              {opt}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default SpecDropdown;
